<template>
  <span v-resize="checkMobile">
    <loading :active.sync="isLoading" :is-full-page="true" color="#1976d2" :background-color="$vuetify.theme.dark ? '#000' : ''"></loading>

    <v-toolbar text :color="$vuetify.theme.dark ? '' : 'white'" class="align-center" :height="isMobile ? 250 : ''">
      <v-flex :class="isMobile ? '' : 'd-flex align-center justify-space-between'">
        <v-toolbar-title>{{ $t("analitics.common_cars") }} {{ $t("custom.number")
          }}{{ report.id }} {{ start_date_formatted }} -
          {{ end_date_formatted }} ({{ getBranchName() }})</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-checkbox v-model="report.with_property" :label="$t('analitics.with_property_short')"
          @click="with_property_warn()" readonly class="mt-5"></v-checkbox>
        <v-checkbox v-model="report.without_partners_expense" :label="$t('analitics.without_partners_expense')"
          readonly class="mt-5 ml-2"></v-checkbox>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          class="mr-2"
          icon
          @click="recalculateReport"
          :loading="isRecalculating"
          :title="$t('analitics.recalculate')"
          :disabled="isRecalculating || isGenerating"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn icon :href="`https://rentprog.${$root.$i18n.locale == 'ru' ? 'ru' : 'com'
          }/${$root.$i18n.locale}/categories/8/guides/25`" target="_blank">
          <v-icon>mdi-help-circle</v-icon>
        </v-btn>
      </v-flex>
    </v-toolbar>
    
    <!-- Индикатор генерации отчета -->
    <v-flex v-if="isGenerating && !generationError" md12 class="mb-4 mt-4">
      <v-alert
        type="info"
        border="left"
        colored-border
        class="mb-4"
      >
        <v-row align="center">
          <v-col class="grow">
            <div class="text-h6">{{ $t('analitics.report_generating') }}</div>
            <div class="text-caption">
              <span v-if="connected" class="success--text">● {{ $t('analitics.connected') }}</span>
              <span v-else class="error--text">● {{ $t('analitics.disconnected') }}</span>
            </div>
          </v-col>
        </v-row>
        <v-progress-linear
          :value="reportProgress"
          color="primary"
          height="20"
          class="mt-3"
          striped
        >
          <template v-slot:default>
            <strong>{{ reportProgress.toFixed(0) }}%</strong>
          </template>
        </v-progress-linear>
      </v-alert>
    </v-flex>
    
    <!-- Ошибка генерации отчета -->
    <v-flex v-if="generationError" md12 class="mb-4 mt-4">
      <v-alert
        type="error"
        border="left"
        colored-border
        class="mb-4"
      >
        <v-row align="center">
          <v-col class="grow">
            <div class="text-h6">{{ $t('analitics.report_generation_error') }}</div>
            <div class="text-body-2 mt-2">{{ generationError }}</div>
          </v-col>
        </v-row>
        <v-btn color="error" outlined class="mt-3" @click="retryGeneration">
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('analitics.try_again') }}
        </v-btn>
      </v-alert>
    </v-flex>
    
    <!-- По машинам -->
    <v-flex v-if="report.state == 'created'" md12 class="d-inline-flex">
      <v-flex md12 class="mt-2">
        <!-- Графики -->
        <v-card-text v-if="showCharts">
          <v-expansion-panels v-model="chartsPanel" multiple>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="subtitle-1 font-weight-medium">
                  <v-icon left color="primary">mdi-chart-bar</v-icon>
                  {{ $t("analitics.visual_analytics") }}
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <!-- Топ-15 по прибыли -->
                <v-flex md12 class="mb-5" v-if="topProfitChartData && topProfitChartData.length > 1">
                  <!-- <h3 class="mb-3">{{ $t("analitics.top_10_profit") }}</h3> -->
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="BarChart"
                    :options="topProfitChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="topProfitChartData"
                  />
                </v-flex>

                <!-- Топ-15 по рентабельности -->
                <v-flex md12 class="mb-5" v-if="topRentabilityChartData && topRentabilityChartData.length > 1">
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="BarChart"
                    :options="topRentabilityChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="topRentabilityChartData"
                  />
                </v-flex>

                <!-- Эффективность парка (Bubble Chart) -->
                <v-flex md12 class="mb-5" v-if="efficiencyChartData && efficiencyChartData.length > 1">
                  <v-row class="mb-2" align="center">
                    <v-col>
                      <!-- <h3>{{ $t("analitics.efficiency_analysis") }}</h3> -->
                    </v-col>
                    <v-col class="text-right">
                      <v-btn
                        icon
                        @click="efficiencyChartDialog = true"
                        :title="$t('custom.expand')"
                      >
                        <v-icon>mdi-fullscreen</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <GChart
                    type="BubbleChart"
                    :data="efficiencyChartData"
                    :options="efficiencyChartOptions"
                    style="width: 100%; height: 500px"
                  />
                </v-flex>

                <!-- Доходы vs Расходы -->
                <v-flex md12 class="mb-5" v-if="incomeExpenseChartData && incomeExpenseChartData.length > 1">
                  <!-- <h3 class="mb-3">{{ $t("analitics.income_expense_top_cars") }}</h3> -->
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="ColumnChart"
                    :options="incomeExpenseChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="incomeExpenseChartData"
                  />
                </v-flex>

                <!-- Структура доходов (Pie Chart) -->
                <v-flex md12 class="mb-5" v-if="incomeStructureChartData && incomeStructureChartData.length > 1">
                  <!-- <h3 class="mb-3">{{ $t("analitics.income_structure_by_cars") }}</h3> -->
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="PieChart"
                    :options="incomeStructureChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="incomeStructureChartData"
                  />
                </v-flex>

                <!-- Средняя месячная прибыль (Line Chart) -->
                <v-flex md12 class="mb-5" v-if="monthlyProfitChartData && monthlyProfitChartData.length > 1">
                  <!-- <h3 class="mb-3">{{ $t("analitics.monthly_profit_loading") }}</h3> -->
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="ComboChart"
                    :options="monthlyProfitChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="monthlyProfitChartData"
                  />
                </v-flex>

                <v-divider class="my-5"></v-divider>
                <h2 class="text-center mb-5 error--text">{{ $t("analitics.worst_performers") }}</h2>

                <!-- Худшие 15 по прибыли -->
                <v-flex md12 class="mb-5" v-if="bottomProfitChartData && bottomProfitChartData.length > 1">
                  <!-- <h3 class="mb-3">{{ $t("analitics.bottom_10_profit") }}</h3> -->
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="BarChart"
                    :options="bottomProfitChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="bottomProfitChartData"
                  />
                </v-flex>

                <!-- Худшие 15 по рентабельности -->
                <v-flex md12 class="mb-5" v-if="bottomRentabilityChartData && bottomRentabilityChartData.length > 1">
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="BarChart"
                    :options="bottomRentabilityChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="bottomRentabilityChartData"
                  />
                </v-flex>

                <!-- Худшие по доходам vs расходам -->
                <v-flex md12 class="mb-5" v-if="incomeExpenseWorstChartData && incomeExpenseWorstChartData.length > 1">
                  <!-- <h3 class="mb-3">{{ $t("analitics.income_expense_worst_cars") }}</h3> -->
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="ColumnChart"
                    :options="incomeExpenseWorstChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="incomeExpenseWorstChartData"
                  />
                </v-flex>

                <!-- Худшие по загруженности -->
                <v-flex md12 class="mb-5" v-if="worstLoadingChartData && worstLoadingChartData.length > 1">
                  <!-- <h3 class="mb-3">{{ $t("analitics.worst_loading_percentage") }}</h3> -->
                  <GChart
                    style="width: 100%; min-height: 400px"
                    type="BarChart"
                    :options="worstLoadingChartOptions"
                    :settings="{
                      packages: ['corechart'],
                      language: this.$i18n.locale,
                    }"
                    :data="worstLoadingChartData"
                  />
                </v-flex>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <v-card-text>
          <v-toolbar text :color="$vuetify.theme.dark ? '' : 'white'">
            <h2>{{ $t("analitics.average_indicators") }}</h2>
          </v-toolbar>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-center">
                    {{ $t("analitics.objects_count") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.average_objects_count") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.incomes") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.expences_on_car") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.profit") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.expences_for_company") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.clear_profit_title") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.average_month_profit") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.rentability_title") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.work_days") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.days_in_rent") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.percent_loading") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.bookings_mileage") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.average_month_mileage") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.km_cost") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.km_income") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.km_income_with_common") }}
                  </th>
                  <th class="text-center">
                    {{ $t("analitics.average_count") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-center">{{ filtered_analitics_data.length }}</td>
                  <td class="text-center">{{ filtered_analitics_data[0] ? filtered_analitics_data[0].cars_numbers : 0 }}</td>
                  <td class="text-center">{{ allIncome() }}</td>
                  <td class="text-center">
                    {{ allExpences() }}
                  </td>
                  <td class="text-center">{{ allProfit() }}</td>
                  <td class="text-center">
                    {{ otherExpencesCounts() }}
                  </td>
                  <td class="text-center">
                    {{ averageProfit() }}
                  </td>
                  <td class="text-center">
                    {{ averageMonthProfit() }}
                  </td>
                  <td class="text-center">
                    {{ averageEffectiveProfit() }}%
                  </td>
                  <td class="text-center">
                    {{ allWorkDays() }}
                  </td>
                  <td class="text-center">
                    {{ allBookingDays() }}
                  </td>
                  <td class="text-center">{{ averageLoading() }}%</td>
                  <td class="text-center">
                    {{ allBookingMileage() }}
                  </td>
                  <td class="text-center">
                    {{ averageBookingMileage() }}
                  </td>
                  <td class="text-center">
                    {{ averageCostKm() }}
                  </td>
                  <td class="text-center">
                    {{ averageIncomeKm() }}
                  </td>
                  <td class="text-center">
                    {{ averageIncomeKmWithCommon() }}
                  </td>
                  <td class="text-center">
                    {{ averageCost() }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
        <v-card-text>
          <v-toolbar text :color="$vuetify.theme.dark ? '' : 'white'" :height="isMobile ? 200 : 150">
            <v-row>
              <v-col cols="12" md="12">
                <h2>{{ $t("analitics.for_cars") }}</h2>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center justify-start">
                <v-autocomplete v-model="selected_cars" clearable :items="cars_codes" multiple
                  :label="$t('custom.object')" hide-no-data hide-details class="mr-2"></v-autocomplete>
                <v-text-field v-model="search" append-icon="mdi-magnify" :label="$t('custom.search')" single-line
                  clearable hide-details></v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center justify-end">
                <v-checkbox v-model="with_sold" :label="$t('analitics.show_unactive')" class="mt-5"></v-checkbox>
                <v-btn icon color="success" class="ml-3" @click="toXlsx" style="cursor: pointer" :title="$t('custom.export_excel')">
                  <v-icon>mdi-file-excel-outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-toolbar>
          <v-data-table :class="!isMobile ? 'mt-5' : 'mt-5 is-mobile'" dense :headers="headers"
            :items="filtered_analitics_data" :loading="isLoading" :loading-text="$t('custom.loading_table')"
            :search="search" :items-per-page="50" :sort-by="['created_at']" mobile-breakpoint="100"
            :sort-desc="[true]" :footer-props="tableFooterProps([20, 50, 100, -1])">
            <template slot="item" slot-scope="props">
              <tr @dblclick="replaceToCar(props.item.car_id)"
                :class="props.item.active ? '' : 'grey--text text--lighten-1'">
                <td nowrap class="text-center">
                  <router-link :to="{ path: `/cars/${props.item.car_id}` }">{{
                    props.item.car_code
                    }}</router-link>
                </td>
                <td nowrap class="text-center">
                  {{ props.item.income_rub }}
                </td>
                <td nowrap class="text-center">
                  {{ props.item.expences_rub }}
                </td>
                <td nowrap class="text-center">
                  {{ props.item.profit }}
                </td>
                <td nowrap class="text-center">
                  {{
                    filtered_analitics_data.length > 0 ? parseFloat(
                      props.item.other_expences_counts /
                      (filtered_analitics_data[0].cars_numbers > 0
                        ? filtered_analitics_data[0].cars_numbers
                        : 1)
                    ).toFixed(2) : '0.00'
                  }}
                </td>
                <td nowrap class="text-center">
                  {{ props.item.average_profit }}
                </td>
                <td nowrap class="text-center" v-if="props.item.work_days > 30">
                  {{
                    parseInt(
                      props.item.average_profit / (props.item.work_days / 30)
                    )
                  }}
                </td>
                <td nowrap class="text-center" v-else>-</td>
                <td nowrap class="text-center" v-if="
                  props.item.work_days > 0 && props.item.purchase_price > 0
                ">
                  {{
                    parseFloat(
                      ((props.item.average_profit /
                        (props.item.work_days / 30)) *
                        12 /
                        props.item.purchase_price) *
                      100
                    ).toFixed(2)
                  }}%
                </td>
                <td nowrap class="text-center" v-else>-</td>
                <td nowrap class="text-center">{{ props.item.purchase_price || 0 }}</td>
                <td nowrap class="text-center">{{ props.item.work_days }}</td>
                <td nowrap class="text-center">
                  {{ props.item.bookings_days }}
                </td>
                <td nowrap class="text-center">
                  {{ props.item.loading_percentage }}%
                </td>
                <td nowrap class="text-center">
                  {{ props.item.bookings_mileage }} км
                </td>
                <td nowrap class="text-center" v-if="props.item.work_days > 30">
                  {{
                    parseFloat(
                      props.item.bookings_mileage /
                      (props.item.work_days / 30)
                    ).toFixed(2)
                  }}
                  {{ $t("custom.km") }}
                </td>
                <td nowrap class="text-center" v-else>-</td>
                <td nowrap class="text-center" v-if="
                  props.item.work_days > 0 &&
                  props.item.bookings_mileage > 0
                ">
                  {{
                    parseFloat(
                      props.item.expences_rub / props.item.bookings_mileage
                    ).toFixed(2)
                  }}
                </td>
                <td nowrap class="text-center" v-else>-</td>
                <td nowrap class="text-center" v-if="props.item.bookings_mileage > 0 && props.item.profit_without_common != null && !isNaN(props.item.profit_without_common)">
                  {{
                    parseFloat(
                      props.item.profit_without_common / props.item.bookings_mileage
                    ).toFixed(2)
                  }}
                </td>
                <td nowrap class="text-center" v-else>-</td>
                <td nowrap class="text-center" v-if="props.item.bookings_mileage > 0 && props.item.average_profit != null && !isNaN(props.item.average_profit)">
                  {{
                    parseFloat(
                      props.item.average_profit / props.item.bookings_mileage
                    ).toFixed(2)
                  }}
                </td>
                <td nowrap class="text-center" v-else>-</td>
                <td nowrap class="text-center">
                  {{ Math.round(props.item.bookings_average_cost)
                  }}
                </td>
                <td nowrap class="text-center" v-if="
                  props.item.purchase_price > 0 && props.item.sale_price > 0
                ">
                  {{
                    props.item.profit -
                    props.item.purchase_price +
                    props.item.sale_price
                  }}
                </td>
                <td nowrap class="text-center" v-else>-</td>
                <td nowrap class="text-center" v-if="
                  props.item.purchase_price > 0 &&
                  props.item.sale_price > 0 &&
                  props.item.bookings_mileage > 0
                ">
                  {{
                    parseFloat(
                      (props.item.profit -
                        props.item.purchase_price +
                        props.item.sale_price) /
                      props.item.bookings_mileage
                    ).toFixed(2)
                  }}
                </td>
                <td nowrap class="text-center" v-else>-</td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <!-- Описание метрик -->
        <v-card-text v-if="showCharts">
          <v-alert
            dense
            border="left"
            colored-border
            type="info"
            elevation="2"
          >
            <div class="subtitle-2 mb-2">
              {{ $t("analitics.metrics_description") }}
            </div>
            <v-list dense class="transparent">
              <v-list-item v-if="$t('analitics.profit_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.profit_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="$t('analitics.clear_profit_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.clear_profit_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="$t('analitics.average_month_profit_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.average_month_profit_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="$t('analitics.rentability_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.rentability_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="$t('analitics.km_cost_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.km_cost_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="$t('analitics.km_income_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.km_income_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="$t('analitics.km_income_with_common_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.km_income_with_common_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="$t('analitics.percent_loading_tooltip')">
                <v-list-item-content>
                  <v-list-item-subtitle class="text-wrap">
                    {{ $t("analitics.percent_loading_tooltip") }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-alert>
        </v-card-text>
      </v-flex>
    </v-flex>
    <v-flex v-else md12 class="text-center mt-10">
      {{ $t("analitics.report_in_progress") }}
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <br />
      <v-btn @click="$router.go()">{{ $t("custom.update") }}</v-btn>
    </v-flex>
    
    <!-- Диалог для полноэкранного просмотра графика эффективности -->
    <v-dialog v-model="efficiencyChartDialog" :retain-focus="false" :fullscreen="isMobile" persistent>
      <v-card>
        <v-toolbar :color="$vuetify.theme.dark ? '' : 'white'">
          <v-toolbar-title>{{ $t("analitics.efficiency_analysis") }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="efficiencyChartDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-5" style="height: calc(100vh - 64px - var(--safe-area-top));">
          <GChart
            v-if="efficiencyChartData && efficiencyChartData.length > 1"
            type="BubbleChart"
            :data="efficiencyChartData"
            :options="efficiencyChartOptionsFullscreen"
            style="width: 100%; height: 100%"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>
<script>
import moment from "moment";
import { setMomentLocale } from "@/utils/localeInfra";
import "moment/locale/ru";
import "moment/locale/en-gb";
import { GChart } from "vue-google-charts";
import { Capacitor } from "@capacitor/core";
import PullToRefresh from "pulltorefreshjs";
import xlsx from "json-as-xlsx";
import { saveAs } from "file-saver";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import CableSubscriber from "@/mixins/CableSubscriber";
export default {
  mixins: [CableSubscriber],
  data() {
    let self = this;
    return {
      report: {},
      analitics_data: [],
      filtered_analitics_data: [],
      selected_cars: [],
      with_sold: false,
      property: true,
      cars_codes: [],
      headers: [
        { text: this.$t("custom.object"), value: "car_code", align: "center" },
        {
          text: this.$t("analitics.incomes"),
          value: "income_rub",
          align: "center",
        },
        {
          text: this.$t("analitics.expences"),
          value: "expences_rub",
          align: "center",
        },
        { text: this.$t("analitics.profit"), value: "profit", align: "center" },
        {
          text: this.$t("analitics.expences_for_company"),
          value: "other_expences_counts",
          align: "center",
        },
        {
          text: this.$t("analitics.clear_profit_title"),
          value: "average_profit",
          align: "center",
        },
        {
          text: this.$t("analitics.average_month_profit"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.rentability_title"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.purchase_price"),
          value: "purchase_price",
          align: "center",
        },
        {
          text: this.$t("analitics.work_days"),
          value: "work_days",
          align: "center",
        },
        {
          text: this.$t("analitics.days_in_rent"),
          value: "bookings_days",
          align: "center",
        },
        {
          text: this.$t("analitics.percent_loading"),
          value: "loading_percentage",
          align: "center",
        },
        {
          text: this.$t("analitics.bookings_mileage"),
          value: "bookings_mileage",
          align: "center",
        },
        {
          text: this.$t("analitics.average_month_mileage"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.km_cost"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.km_income"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.km_income_with_common"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.average_count"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.with_car"),
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("analitics.with_car_km"),
          sortable: false,
          align: "center",
        },
      ],
      search: "",
      isMobile: false,
      isLoading: false,
      error: "",
      showCharts: false,
      chartsPanel: [0], // Открываем панель с графиками по умолчанию
      // WebSocket статус
      isGenerating: false,
      isRecalculating: false,
      connected: false,
      reportProgress: 0,
      generationError: null,
      // Диалог для полноэкранного просмотра графика
      efficiencyChartDialog: false,
      // Данные о филиалах
      branches: [],
      current_branch_name: ''
    };
  },
  mounted() {
    let self = this;
    const ptr = PullToRefresh.init({
      mainElement: "body",
      distIgnore: 100,
      instructionsPullToRefresh: this.$t("custom.pull_to_refresh"),
      instructionsReleaseToRefresh: this.$t("custom.release_to_refresh"),
      instructionsRefreshing: this.$t("custom.refreshing"),
    });
  },
  beforeDestroy() {
    // Отписываемся от WebSocket при уничтожении компонента
    this.unsubscribeFromReport()
  },
  created() {
    setMomentLocale(this.$i18n.locale);
    if (
      this.$store.getters.isLoggedIn &&
      (this.$store.getters.isSuperAdmin ||
        this.$store.getters.isAdmin ||
        this.$store.getters.isGuest)
    ) {
      this.isLoading = true;
      this.loadReport();
    } else {
      this.$router.push({ path: `/dashboard` }).catch((err) => { });
      this.$notify(this.$t("errors.only_admins"), "", "warning", 5000);
    }
  },
  watch: {
    with_sold() {
      if (this.with_sold) {
        this.filtered_analitics_data = this.analitics_data;
      } else {
        this.filtered_analitics_data = this.analitics_data.filter((item) => {
          return item.active == true;
        });
      }
    },
    selected_cars() {
      let self = this;
      if (this.selected_cars.length > 0) {
        this.filtered_analitics_data = this.analitics_data.filter((car) =>
          self.selected_cars.includes(car.car_code)
        );
      } else {
        this.filtered_analitics_data = [];
        this.analitics_data.forEach((car) =>
          self.filtered_analitics_data.push(car)
        );
      }
    },
  },
  computed: {
    start_date_formatted() {
      return moment(this.report.start_date).format("DD.MM.YYYY");
    },
    end_date_formatted() {
      return moment(this.report.end_date).format("DD.MM.YYYY");
    },
    currency() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currency
      ) {
        return this.$store.getters.getCompany.company.currency;
      } else {
        return "";
      }
    },
    currencies() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currencies
      ) {
        return this.$store.getters.getCompany.company.currencies;
      } else {
        return [];
      }
    },
    // Опции графиков с поддержкой темной темы
    topProfitChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.top_10_profit"),
        chartArea: { width: "70%", height: "80%" },
        height: 600,
        hAxis: {
          title: this.$t("analitics.profit"),
          minValue: 0,
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("analitics.rental_objects"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000", fontSize: 12 },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          showTextEvery: 1,
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#4CAF50"],
      };
    },
    efficiencyChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.efficiency_analysis"),
        hAxis: {
          title: this.$t("analitics.percent_loading"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("analitics.clear_profit_title"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        bubble: {
          textStyle: { fontSize: 11, color: isDark ? "#FFFFFF" : "#000000" },
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#2196F3"],
      };
    },
    efficiencyChartOptionsFullscreen() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.efficiency_analysis"),
        chartArea: { width: "85%", height: "80%" },
        hAxis: {
          title: this.$t("analitics.percent_loading"),
          textStyle: { fontSize: 14, color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { fontSize: 16, color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("analitics.clear_profit_title"),
          textStyle: { fontSize: 14, color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { fontSize: 16, color: isDark ? "#FFFFFF" : "#000000" },
        },
        bubble: {
          textStyle: { fontSize: 16, color: isDark ? "#FFFFFF" : "#000000" },
        },
        titleTextStyle: { fontSize: 20, color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { fontSize: 14, color: isDark ? "#FFFFFF" : "#000000" },
          position: 'right'
        },
        colors: ["#2196F3"],
        explorer: {
          actions: ['dragToZoom', 'rightClickToReset'],
          axis: 'horizontal',
          keepInBounds: true,
          maxZoomIn: 8.0
        },
        tooltip: {
          textStyle: { fontSize: 14 }
        }
      };
    },
    incomeExpenseChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.income_expense_top_cars"),
        chartArea: { width: "70%" },
        hAxis: {
          title: this.$t("analitics.rental_objects"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("custom.sum"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#4CAF50", "#F44336"],
        isStacked: false,
      };
    },
    incomeStructureChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.income_structure_by_cars"),
        pieHole: 0.4,
        sliceVisibilityThreshold: 0.02,
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
      };
    },
    monthlyProfitChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.monthly_profit_loading"),
        seriesType: "bars",
        series: { 
          0: { targetAxisIndex: 0 },  // Прибыль на левой оси
          1: { type: "line", targetAxisIndex: 1 }  // Загрузка на правой оси
        },
        hAxis: {
          title: this.$t("custom.object"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxes: {
          0: {
            title: this.$t("analitics.profit"),
            textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
            titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          },
          1: {
            title: this.$t("analitics.percent_loading"),
            textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
            titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
            minValue: 0,
            maxValue: 100,
            format: '#\'%\''
          }
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#4CAF50", "#FF9800"],
      };
    },
    // Топ-15 по прибыли
    topProfitChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active)
        .sort((a, b) => b.profit - a.profit)
        .slice(0, 15);

      const data = [[this.$t("custom.object"), this.$t("analitics.profit")]];
      sorted.forEach((item) => {
        data.push([item.car_code, parseFloat(item.profit)]);
      });
      return data;
    },
    topRentabilityChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.top_10_rentability"),
        chartArea: { width: "70%", height: "80%" },
        height: 600,
        hAxis: {
          title: this.$t("analitics.rentability_title") + " (%)",
          minValue: 0,
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("analitics.rental_objects"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000", fontSize: 12 },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          showTextEvery: 1,
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#2196F3"],
      };
    },
    topRentabilityChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active && item.work_days > 0 && item.purchase_price > 0)
        .map((item) => {
          const monthly_profit = item.average_profit / (item.work_days / 30);
          const rentability = (monthly_profit * 12 / item.purchase_price) * 100;
          return { ...item, rentability };
        })
        .sort((a, b) => b.rentability - a.rentability)
        .slice(0, 15);

      const data = [[this.$t("custom.object"), this.$t("analitics.rentability_title")]];
      sorted.forEach((item) => {
        data.push([item.car_code, parseFloat(item.rentability.toFixed(2))]);
      });
      return data;
    },
    // Эффективность парка (Bubble Chart)
    efficiencyChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const data = [
        [
          "ID",
          this.$t("analitics.percent_loading"),
          this.$t("analitics.clear_profit_title"),
          this.$t("custom.object"),
          this.$t("analitics.bookings_mileage"),
        ],
      ];
      this.analitics_data
        .filter((item) => item.active && item.work_days > 0)
        .forEach((item) => {
          data.push([
            item.car_code,
            parseFloat(item.loading_percentage),
            parseFloat(item.average_profit),
            item.car_code,
            parseFloat(item.bookings_mileage),
          ]);
        });
      return data;
    },
    // Доходы vs Расходы топ машин
    incomeExpenseChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active)
        .sort((a, b) => b.profit - a.profit)
        .slice(0, 15);

      const data = [
        [
          this.$t("custom.object"),
          this.$t("analitics.incomes"),
          this.$t("analitics.expences"),
        ],
      ];
      sorted.forEach((item) => {
        data.push([
          item.car_code,
          parseFloat(item.income_rub),
          parseFloat(item.expences_rub),
        ]);
      });
      return data;
    },
    // Структура доходов
    incomeStructureChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const data = [[this.$t("custom.object"), this.$t("analitics.income")]];
      const sorted = [...this.analitics_data]
        .filter((item) => item.active && item.income_rub > 0)
        .sort((a, b) => b.income_rub - a.income_rub)
        .slice(0, 15);

      sorted.forEach((item) => {
        data.push([item.car_code, parseFloat(item.income_rub)]);
      });
      return data;
    },
    // Месячная прибыль и загруженность
    monthlyProfitChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active && item.work_days > 30)
        .sort((a, b) => b.profit - a.profit)
        .slice(0, 15);

      const data = [
        [
          this.$t("custom.object"),
          this.$t("analitics.average_month_profit"),
          this.$t("analitics.percent_loading"),
        ],
      ];
      sorted.forEach((item) => {
        const monthlyProfit = parseFloat(
          item.average_profit / (item.work_days / 30)
        ).toFixed(2);
        data.push([
          item.car_code,
          parseFloat(monthlyProfit),
          parseFloat(item.loading_percentage),
        ]);
      });
      return data;
    },
    // === Графики худших показателей ===
    bottomProfitChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.bottom_10_profit"),
        chartArea: { width: "70%", height: "80%" },
        height: 600,
        hAxis: {
          title: this.$t("analitics.profit"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("analitics.rental_objects"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000", fontSize: 12 },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          showTextEvery: 1,
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#F44336"],
      };
    },
    bottomProfitChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active)
        .sort((a, b) => a.profit - b.profit)
        .slice(0, 15);

      const data = [[this.$t("custom.object"), this.$t("analitics.profit")]];
      sorted.forEach((item) => {
        data.push([item.car_code, parseFloat(item.profit)]);
      });
      return data;
    },
    bottomRentabilityChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.bottom_10_rentability"),
        chartArea: { width: "70%", height: "80%" },
        height: 600,
        hAxis: {
          title: this.$t("analitics.rentability_title") + " (%)",
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("analitics.rental_objects"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000", fontSize: 12 },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          showTextEvery: 1,
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#FF9800"],
      };
    },
    bottomRentabilityChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active && item.work_days > 0 && item.purchase_price > 0)
        .map((item) => {
          const monthly_profit = item.average_profit / (item.work_days / 30);
          const rentability = (monthly_profit * 12 / item.purchase_price) * 100;
          return { ...item, rentability };
        })
        .sort((a, b) => a.rentability - b.rentability)
        .slice(0, 15);

      const data = [[this.$t("custom.object"), this.$t("analitics.rentability_title")]];
      sorted.forEach((item) => {
        data.push([item.car_code, parseFloat(item.rentability.toFixed(2))]);
      });
      return data;
    },
    incomeExpenseWorstChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.income_expense_worst_cars"),
        chartArea: { width: "70%" },
        hAxis: {
          title: this.$t("custom.object"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("custom.sum"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#4CAF50", "#F44336"],
        isStacked: false,
      };
    },
    incomeExpenseWorstChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active)
        .sort((a, b) => a.profit - b.profit)
        .slice(0, 15);

      const data = [
        [
          this.$t("custom.object"),
          this.$t("analitics.incomes"),
          this.$t("analitics.expences"),
        ],
      ];
      sorted.forEach((item) => {
        data.push([
          item.car_code,
          parseFloat(item.income_rub),
          parseFloat(item.expences_rub),
        ]);
      });
      return data;
    },
    worstLoadingChartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        title: this.$t("analitics.worst_loading_percentage"),
        chartArea: { width: "70%", height: "80%" },
        height: 600,
        hAxis: {
          title: this.$t("analitics.percent_loading"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        vAxis: {
          title: this.$t("analitics.rental_objects"),
          textStyle: { color: isDark ? "#FFFFFF" : "#000000", fontSize: 12 },
          titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
          showTextEvery: 1,
        },
        titleTextStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
        legend: {
          textStyle: { color: isDark ? "#FFFFFF" : "#000000" },
        },
        colors: ["#FF9800"],
      };
    },
    worstLoadingChartData() {
      if (!this.analitics_data || this.analitics_data.length === 0) {
        return [];
      }
      const sorted = [...this.analitics_data]
        .filter((item) => item.active && item.work_days > 0)
        .sort((a, b) => a.loading_percentage - b.loading_percentage)
        .slice(0, 15);

      const data = [[this.$t("custom.object"), this.$t("analitics.percent_loading")]];
      sorted.forEach((item) => {
        data.push([item.car_code, parseFloat(item.loading_percentage)]);
      });
      return data;
    },
  },
  methods: {
    getBranchName() {
      if (this.report.all_branches) {
        return this.$t('custom.all_branches');
      }
      
      if (this.report.company_id) {
        const branch = this.branches.find(b => b.id === this.report.company_id);
        return branch ? branch.name : this.current_branch_name;
      }
      
      return this.current_branch_name;
    },
    loadReport() {
      this.axios
        .get(`/api/v1/cars_reports/${this.$route.params.query}`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.report = response.data;
          this.branches = response.data.branches || [];
          this.current_branch_name = response.data.current_branch_name || '';
          
          // Если отчет в статусе initial или pending, подписываемся на обновления
          if (this.report.state === 'initial' || this.report.state === 'pending') {
            this.isGenerating = true
            this.subscribeToReportUpdates()
          }
          
          this.cars_codes = [];
          if (response.data.state == "created") {
            response.data.cars_data.forEach((car) =>
              this.cars_codes.push(car.car_code)
            );
            this.analitics_data = response.data.cars_data;
            this.filtered_analitics_data = this.analitics_data.filter(
              (item) => {
                return item.active == true;
              }
            );
            // Включаем отображение графиков после загрузки данных
            this.showCharts = true;
          }
          this.isLoading = false;
        })
        .catch((error) => {
          this.setError(error, this.$t("errors.cars_report_load"));
          console.log(error);
          this.isLoading = false;
        });
    },
    
    // FT-066: ReportsChannel handlers (moved from the old channels option; this-rebinding via
    // arrows in subscribeToReportUpdates). Bodies are 1:1 with the former declarative channel.
    onReportConnected() {
      this.connected = true
    },
    onReportDisconnected() {
      this.connected = false
    },
    onReportReceived(data) {
      if (data.report_id === this.report.id) {
        this.reportProgress = data.progress

        if (data.status === 'completed') {
          this.isGenerating = false
          this.loadReport()

          this.$notify(
            this.$t('analitics.report_ready'),
            this.$t('analitics.report_generated_successfully'),
            'success',
            5000
          )
        }

        if (data.status === 'failed') {
          this.isGenerating = false
          this.generationError = data.error || this.$t('analitics.report_generation_failed')

          this.$notify(this.$t('errors.error'), this.generationError, 'error', 5000)
        }
      }
    },

    subscribeToReportUpdates() {
      if (this.report && this.report.id) {
        // FT-066 switch pattern: drop any prior sub before re-subscribing.
        this.$cableUnsubscribe(this.reportSub)
        this.reportSub = this.$cableSubscribe(
          {
            channel: 'ReportsChannel',
            report_type: 'cars',
            report_id: this.report.id
          },
          {
            connected: () => this.onReportConnected(),
            disconnected: () => this.onReportDisconnected(),
            received: (data) => this.onReportReceived(data)
          }
        )
      }
    },

    unsubscribeFromReport() {
      this.$cableUnsubscribe(this.reportSub)
      this.reportSub = null
    },
    
    recalculateReport() {
      if (!this.report || !this.report.id || this.isRecalculating) {
        return
      }

      this.isRecalculating = true
      this.generationError = null
      this.isGenerating = true
      this.reportProgress = 0

      this.unsubscribeFromReport()

      this.axios
        .post(`/api/v1/cars_reports/${this.report.id}/recalculate`, {}, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then(() => {
          if (this.report) {
            this.report.state = 'pending'
          }
          this.subscribeToReportUpdates()
          this.$notify(
            this.$t('analitics.report_recalculation_started'),
            '',
            'success',
            3000
          )
        })
        .catch((error) => {
          this.isGenerating = false
          const message =
            (error.response && error.response?.data && error.response?.data.error) ||
            this.$t('analitics.report_recalculation_failed')
          let localizedMessage = message
          if (message === 'Report is already being processed') {
            localizedMessage = this.$t('analitics.report_already_processing')
          }
          this.generationError = localizedMessage
          this.$notify(this.$t('errors.error'), localizedMessage, 'error', 5000)
        })
        .finally(() => {
          this.isRecalculating = false
        })
    },

    retryGeneration() {
      // Сбрасываем ошибку и перезагружаем отчет
      this.generationError = null
      this.isGenerating = true
      this.reportProgress = 0
      this.loadReport()
    },
    
    allIncome() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "0.00";
      }
      let income_rub = 0.0;
      this.filtered_analitics_data.forEach((item) =>
        item.income_counts.forEach((count) => (income_rub += count.sum))
      );
      return parseFloat(
        income_rub /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0)
      ).toFixed(2);
    },
    allProfit() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "0.00";
      }
      let profit = 0.0;
      this.filtered_analitics_data.forEach((item) => (profit += item.profit));
      return parseFloat(
        profit /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0)
      ).toFixed(2);
    },
    allExpences() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "0.00";
      }
      let expences = 0.0;
      this.filtered_analitics_data.forEach((item) =>
        item.expences_counts.forEach((count) => (expences += count.sum))
      );
      return parseFloat(
        expences /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0)
      ).toFixed(2);
    },
    averageProfit() {
      return parseFloat(this.allProfit() - this.otherExpencesCounts()).toFixed(2);
    },
    otherExpencesCounts() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "0.00";
      }
      return parseFloat(
        this.filtered_analitics_data[0].other_expences_counts /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0)
      ).toFixed(2);
    },
    averageMonthProfit() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return 0;
      }
      let profit = 0.0;
      this.filtered_analitics_data.forEach((item) => {
        if (item.work_days > 30.0) {
          profit += Math.round(item.average_profit / (item.work_days / 30));
        }
      });
      if (
        profit &&
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0) > 0
      ) {
        profit =
          profit /
          (this.filtered_analitics_data[0].cars_numbers > 0
            ? this.filtered_analitics_data[0].cars_numbers
            : 1.0);
      }
      return Math.round(profit);
    },
    averageEffectiveProfit() {
      let profit = 0.0;
      let count = 0;
      this.filtered_analitics_data.forEach((item) => {
        if (item.work_days > 0 && item.purchase_price > 0) {
          // Классическая формула рентабельности: (Среднемесячная прибыль × 12 / Стоимость) × 100%
          const monthly_profit = item.average_profit / (item.work_days / 30);
          const rentability = (monthly_profit * 12 / item.purchase_price) * 100;
          if (rentability > 0) {
            profit += rentability;
            count++;
          }
        }
      });
      if (count > 0) {
        profit = profit / count;
      }
      return parseFloat(profit.toFixed(2));
    },
    allWorkDays() {
      let days = 0;
      this.filtered_analitics_data.forEach((item) => (days += item.work_days));
      return days;
    },
    allBookingDays() {
      let days = 0;
      this.filtered_analitics_data.forEach((item) => (days += item.bookings_days));
      return days;
    },
    averageLoading() {
      // Используем уже посчитанные суммы
      const totalWorkDays = this.allWorkDays();
      const totalBookingDays = this.allBookingDays();
      
      if (totalWorkDays > 0) {
        return ((totalBookingDays / totalWorkDays) * 100).toFixed(2);
      }
      return "0.00";
    },
    allBookingMileage() {
      let bookings_mileage = 0;
      this.filtered_analitics_data.forEach(
        (item) => (bookings_mileage += item.bookings_mileage)
      );
      return bookings_mileage;
    },
    averageBookingMileage() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "0.00";
      }
      let loading = 0.0;

      this.filtered_analitics_data.forEach((item) => {
        if (item.work_days > 0 && item.bookings_mileage > 0) {
          loading += item.bookings_mileage / (item.work_days / 30.0);
        }
      });
      return parseFloat(
        loading /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0)
      ).toFixed(2);
    },
    averageCostKm() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "0.00";
      }
      let loading = 0.0;

      this.filtered_analitics_data.forEach((item) => {
        if (item.work_days > 0 && item.bookings_mileage > 0) {
          loading += item.expences_rub / item.bookings_mileage;
        }
      });
      loading =
        loading /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0);
      return parseFloat(loading).toFixed(2);
    },
    averageIncomeKm() {
      // Прибыль на км БЕЗ общих расходов
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "-";
      }
      let loading = 0.0;

      this.filtered_analitics_data.forEach((item) => {
        if (item.work_days > 0 && item.bookings_mileage > 0 && item.profit_without_common != null) {
          const value = item.profit_without_common / item.bookings_mileage;
          if (!isNaN(value)) {
            loading += value;
          }
        }
      });
      loading =
        loading /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0);
      return isNaN(loading) ? '-' : parseFloat(loading).toFixed(2);
    },
    averageIncomeKmWithCommon() {
      // Прибыль на км С общими расходами
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "-";
      }
      let loading = 0.0;

      this.filtered_analitics_data.forEach((item) => {
        if (item.work_days > 0 && item.bookings_mileage > 0 && item.average_profit != null) {
          const value = item.average_profit / item.bookings_mileage;
          if (!isNaN(value)) {
            loading += value;
          }
        }
      });
      loading =
        loading /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0);
      return isNaN(loading) ? '-' : parseFloat(loading).toFixed(2);
    },
    averageCost() {
      if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
        return "0.00";
      }
      let loading = 0.0;
      this.filtered_analitics_data.forEach(
        (item) => (loading += Math.round(item.bookings_average_cost))
      );
      loading =
        loading /
        (this.filtered_analitics_data[0].cars_numbers > 0
          ? this.filtered_analitics_data[0].cars_numbers
          : 1.0);
      return parseFloat(loading).toFixed(2);
    },
    with_property_warn() {
      this.$notify(this.$t("errors.with_property_warn"), "", "warning");
    },
    findCurrencySymbol(id) {
      if (id != null) {
        if (this.currencies.find((currency) => currency.id == id)) {
          return this.currencies.find((currency) => currency.id == id).symbol;
        }
      }
    },
    replaceToCar(car) {
      this.$router.push({ path: `/cars/${car.id}` });
    },
    checkMobile() {
      if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
        this.isMobile = true;
        return true;
      } else {
        this.isMobile = false;
        return false;
      }
    },
    
    toXlsx() {
      let data = [
        {
          sheet: this.$t("analitics.for_cars"),
          columns: [
            { label: this.$t("custom.object"), value: "car_code" },
            { label: this.$t("analitics.incomes"), value: "income_rub" },
            { label: this.$t("analitics.expences"), value: "expences_rub" },
            { label: this.$t("analitics.profit"), value: "profit" },
            {
              label: this.$t("analitics.expences_for_company"),
              value: (row) => {
                if (!this.filtered_analitics_data || this.filtered_analitics_data.length === 0) {
                  return "0.00";
                }
                return parseFloat(
                  row.other_expences_counts /
                  (this.filtered_analitics_data[0].cars_numbers > 0
                    ? this.filtered_analitics_data[0].cars_numbers
                    : 1)
                ).toFixed(2);
              }
            },
            { label: this.$t("analitics.clear_profit_title"), value: "average_profit" },
            {
              label: this.$t("analitics.average_month_profit"),
              value: (row) => {
                if (row.work_days > 30) {
                  return parseInt(row.average_profit / (row.work_days / 30));
                }
                return "-";
              }
            },
            {
              label: this.$t("analitics.investment_100k"),
              value: (row) => {
                if (row.work_days > 0 && row.purchase_price > 0) {
                  return parseFloat(
                    ((row.average_profit / (row.work_days / 30)) * 12 / row.purchase_price) * 100
                  ).toFixed(2) + "%";
                }
                return "-";
              }
            },
            { label: this.$t("analitics.purchase_price"), value: (row) => row.purchase_price || 0 },
            { label: this.$t("analitics.work_days"), value: "work_days" },
            { label: this.$t("analitics.days_in_rent"), value: "bookings_days" },
            {
              label: this.$t("analitics.percent_loading"),
              value: (row) => {
                return row.loading_percentage ? row.loading_percentage + "%" : "-";
              }
            },
            { label: this.$t("analitics.bookings_mileage"), value: "bookings_mileage" },
            {
              label: this.$t("analitics.average_month_mileage"),
              value: (row) => {
                if (row.work_days > 30) {
                  return parseInt(row.bookings_mileage / (row.work_days / 30));
                }
                return "-";
              }
            },
            {
              label: this.$t("analitics.km_cost"),
              value: (row) => {
                if (row.bookings_mileage > 0) {
                  return parseFloat(row.expences_rub / row.bookings_mileage).toFixed(2);
                }
                return "-";
              }
            },
            {
              label: this.$t("analitics.km_income"),
              value: (row) => {
                if (row.bookings_mileage > 0) {
                  return parseFloat(row.profit_without_common / row.bookings_mileage).toFixed(2);
                }
                return "-";
              }
            },
            {
              label: this.$t("analitics.km_income_with_common"),
              value: (row) => {
                if (row.bookings_mileage > 0) {
                  return parseFloat(row.average_profit / row.bookings_mileage).toFixed(2);
                }
                return "-";
              }
            },
            {
              label: this.$t("analitics.average_count"),
              value: (row) => {
                return Math.round(row.bookings_average_cost);
              }
            },
            {
              label: this.$t("analitics.with_car"),
              value: (row) => {
                if (row.purchase_price > 0 || row.sale_price > 0) {
                  return row.purchase_price + row.sale_price;
                }
                return "-";
              }
            },
            {
              label: this.$t("analitics.with_car_km"),
              value: (row) => {
                if (row.bookings_mileage > 0 && (row.purchase_price > 0 || row.sale_price > 0)) {
                  return parseFloat(
                    (row.average_profit + row.purchase_price + row.sale_price) / row.bookings_mileage
                  ).toFixed(2);
                }
                return "-";
              }
            },
          ],
          content: this.filtered_analitics_data,
        },
      ];
      
      let settings = {
        fileName: `objects_report_${this.report.id}`,
        extraLength: 3,
        writeMode: "write",
        writeOptions: {
          bookType: "xlsx",
          type: "array",
        },
        RTL: false,
      };
      
      let wbout = xlsx(data, settings);
      var blob_data = new Blob([new Uint8Array(wbout)], { type: "application/octet-stream" });
      
      if (!Capacitor.isNativePlatform()) {
        // Для веб-версии
        saveAs(blob_data, `cars_report_${this.report.id}.xlsx`);
      } else {
        // Для мобильной версии
        if (Filesystem.checkPermissions()) {
          const getBase64FromBlob = async (blob) => {
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = () => {
                const base64data = reader.result;
                resolve(base64data);
              }
            });
          }
          
          getBase64FromBlob(blob_data).then((base64) => {
            async function writeFile() {
              return await Filesystem.writeFile({
                path: `cars_report_${this.report.id}.xlsx`,
                data: base64,
                directory: Directory.Documents,
              });
            }
            
            writeFile().then((getUriResult) => {
              let shareFile = async () => {
                await Share.share({
                  title: 'Download document',
                  files: [getUriResult.uri],
                }).catch((error) => {
                  console.log("shareFile", error)
                });
              };
              shareFile();
            }).catch((error) => {
              console.log("shareFile", error)
            });
          });
        } else {
          Filesystem.requestPermissions();
        }
      }
    },
  },
  components: {
    GChart
  },
};
</script>
<style>
.is-mobile .v-data-footer__select {
  margin-right: 0 !important;
  margin-left: 0 !important;
}

.is-mobile .v-data-footer__icons-before {
  margin: 0 auto !important;
}

.is-mobile .v-data-footer__icons-after {
  margin: 0 auto !important;
}
</style>
